"use client";

import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import * as GiftService from "@/services/gift";
import { getShippingSimulate } from "@/services/shipping";
import { updateFreightData } from "@/services/freight";
import * as MESSAGES from "@/utils/checkout/constants/messages";
import { STATUS } from "@/utils/checkout/constants/product";
import { debounce } from "@/utils/checkout/eventFrequency";
import { useUtmParams } from "@/hooks/useUtmParams";
import {
  removeProduct,
  updateQuantity,
  clearMessages,
  createNewCart,
  addProduct,
  getCart
} from "@/domain/Checkout/checkout.service";

import {
  getDonationData,
  getDonationDataArray
} from "@/utils/functions/getCartID";
import { getCartID } from "@/utils/functions";

import type { Ong } from "@/domain/Ongs/ongs.types";
import type { DonationData } from "@/types/donation";
import type { SnackbarProps } from "@/components/shared/Snackbar/snackbar.types";
import type { ProductToUpdate } from "../checkout.types";
import type { ProductWithEvent, SetGiftActiveParams } from "@/types/cart";
import type {
  ClientProfileData,
  LogisticsInfo,
  Order,
  OrderItem,
  SelectableGiftItem,
  ShippingData,
  Sla
} from "@/types/order";

import { DELIVERY_CHANNEL_OPTIONS } from "@/utils/checkout/constants/shipping";
import { addCoupon, removeCoupon } from "@/services/coupon";
import { setMarketingData } from "@/services/config";

interface UseCartProps {
  orderForm: Order;
  messages?: any[];
  updateOrderForm: (data: any) => void; // eslint-disable-line
  setSnackbarData: (snackbarData: SnackbarProps) => void; // eslint-disable-line
  ong: Ong;
}

export const useCart = ({
  ong,
  orderForm,
  updateOrderForm,
  setSnackbarData
}: UseCartProps) => {
  let fila: ProductWithEvent[] = [];
  let time: number | null;

  const { utmParams } = useUtmParams();

  const orderFormId = orderForm?.orderFormId;

  const [isLoadingCart, setLoadingCart] = useState(false);
  const [coupon, setCoupon] = useState<string>(
    orderForm?.marketingData?.coupon || ""
  );
  const [errorCoupon, setErrorCoupon] = useState<string>("");
  const [cartID, setCartID] = useState<string>("");
  const [notIdentifiedSla, setNotIdentifiedSla] = useState<boolean>(false);

  const [donationData, setDonationData] = useState<DonationData>({
    cartID: "",
    ongId: 0,
    ongName: "",
    zipcode: "",
    pickupId: ""
  });

  useEffect(() => {
    const donationDataStorage = getDonationData(ong.id);
    setDonationData(donationDataStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateQuantity = async (
    quantity: number,
    product: OrderItem
  ): Promise<void> => {
    fila = [
      ...fila.filter((item) => item.product.index !== product.index),
      { quantity, product, eventDecrease: quantity < product.quantity }
    ];

    const timestamp: number = Date.now();
    time = timestamp;

    triggerUpdateQuantity(fila, timestamp, orderFormId);
  };

  const triggerUpdateQuantity = debounce(
    async (
      products: ProductToUpdate[],
      timestamp: number,
      orderFormId: string
    ): Promise<void> => {
      try {
        setLoadingCart(true);
        const data = await updateQuantity(products, orderFormId);
        updateOrderForm(data);

        setSnackbarData({
          show: true,
          message: "Produto atualizado com sucesso",
          variant: "success",
          onCloseButton: closeSnackbar
        });

        /* await products.forEach((product) => {
            product.eventDecrease
              ? GA.removeFromCart(product, { sellers, value }, subscriptionData)
              : GA.addToCart(
                  "Aumento de quantidade",
                  product,
                  {
                    sellers,
                    value
                  },
                  subscriptionData
                );
          }); */

        if (time === timestamp) {
          //GA.cartData(data);
          time = null;
        }
      } catch (error) {
        errorMessageDefault();
        console.error("Error:", error);
      } finally {
        fila = [];
        setLoadingCart(false);
      }
    }
  );

  const setLoading = (isLoading: boolean) => {
    setLoadingCart(isLoading);
  };

  /*
   * Monta o cesto de doação vinculado com a ONG e a Loja
   */
  useEffect(() => {
    const cartID = getCartID(ong.id);

    if (!orderForm || orderForm?.orderFormId !== cartID) return;

    setCoupon(orderForm?.marketingData?.coupon || "");

    if (
      orderForm?.items?.length &&
      donationData?.zipcode &&
      (!orderForm?.shippingData?.address?.number ||
        orderForm?.shippingData?.address?.postalCode !== donationData.zipcode ||
        (orderForm?.shippingData?.logisticsInfo?.length &&
          !orderForm.shippingData?.logisticsInfo[0].selectedSla?.includes(
            String(donationData.pickupId)
          )))
    ) {
      mountDonationCart(donationData);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderForm, donationData]);

  useEffect(() => {
    if (!orderForm?.messages?.length) return;

    const messages = orderForm?.messages || [];

    const messageQuantityLimitReached = messages?.some(
      (item) =>
        item.code === MESSAGES.CODE_QUANTITY_NOT_AVAILABLE ||
        item.code === MESSAGES.CODE_QUANTITY_LIMIT_REACHED
    );

    if (messageQuantityLimitReached) {
      setSnackbarData({
        show: true,
        message:
          "Estoque limitado deste produto. A quantidade disponível será ajustada automaticamente em seu carrinho.",
        variant: "warning",
        actionText: "Fechar",
        onActionButton: () => {
          clearOrderFormMessages();
          closeSnackbar();
        },
        onCloseButton: () => {
          clearOrderFormMessages();
          closeSnackbar();
        },
        onClose: () => {
          clearOrderFormMessages();
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderForm?.messages]);

  const errorMessageDefault = () => {
    setSnackbarData({
      show: true,
      message: MESSAGES.TRY_AGAIN_LATER,
      variant: "error",
      onCloseButton: closeSnackbar
    });
  };

  const setGiftquantity = debounce(
    async (
      quantity: number,
      giftId: string,
      availableGifts: SelectableGiftItem[]
    ): Promise<void> => {
      try {
        if (!orderFormId) return;

        setLoading(true);

        const data = await GiftService.updateGift(
          orderFormId,
          giftId,
          quantity,
          availableGifts
        );
        updateOrderForm(data);
      } catch (error) {
        setSnackbarData({
          show: true,
          message: "Não foi possível atualizar a quantidade do presente",
          variant: "error",
          onCloseButton: closeSnackbar
        });
        console.error("Error updating gift quantity:", error);
      } finally {
        setLoading(false);
      }
    },
    500
  );

  const setGiftActive = debounce(
    async (
      condition: SetGiftActiveParams["condition"],
      giftId: SelectableGiftItem["id"],
      availableGifts: SelectableGiftItem[]
    ): Promise<void> => {
      if (!orderFormId) return;

      const api = condition
        ? GiftService.addGift(orderFormId, giftId, availableGifts)
        : GiftService.removeGift(orderFormId, giftId);

      try {
        setLoading(true);
        const data = await api;
        updateOrderForm(data);
      } catch (error) {
        setSnackbarData({
          show: true,
          message: "Não foi possível adicionar o presente",
          variant: "error",
          onCloseButton: closeSnackbar
        });
        console.error("Error setting gift active:", error);
      } finally {
        setLoading(false);
      }
    },
    500
  );

  const closeSnackbar = () => {
    setSnackbarData({ show: false, message: "", variant: undefined });
  };

  const handleUndoProduct = async (
    orderFormId: string,
    product: OrderItem[]
  ): Promise<void> => {
    try {
      setLoadingCart(true);

      const data = await addProduct(orderFormId, product);
      updateOrderForm(data);
      setSnackbarData({
        show: true,
        message: "Produto adicionado novamente com sucesso",
        variant: "success",
        onCloseButton: closeSnackbar
      });

      // GA.cartData(data)
    } catch (error) {
      setSnackbarData({
        show: true,
        message: "Não foi possível adicionar o produto novamente",
        variant: "error",
        onCloseButton: closeSnackbar
      });
    } finally {
      setLoadingCart(false);
    }
  };

  const handleRemoveProduct = async (product: OrderItem): Promise<void> => {
    try {
      if (!orderFormId) return;

      setLoadingCart(true);

      const data = await removeProduct(product, orderFormId);
      updateOrderForm(data);

      // GA.cartData(data)
      setSnackbarData({
        show: true,
        message: "Produto removido com sucesso",
        variant: "success",
        actionText: "Desfazer",
        onActionButton: () => handleUndoProduct(orderFormId, [{ ...product }]),
        onCloseButton: closeSnackbar
      });
    } catch (error) {
      setSnackbarData({
        show: true,
        message: "Não foi possível remover o produto",
        variant: "error",
        onCloseButton: closeSnackbar
      });
      console.error("Error removing product:", error);
    } finally {
      setLoadingCart(false);
    }
  };

  const handleSetCoupon = async (coupon: string) => {
    setErrorCoupon("");

    if (coupon === "") {
      return setErrorCoupon("Campo obrigatório");
    }

    try {
      setLoading(true);

      const data = await addCoupon(orderFormId, coupon);
      console.log(" addCoupon data", data);
      updateOrderForm(data);
      setCoupon(coupon);
      setSnackbarData({
        show: true,
        message: "Cupom aplicado com sucesso",
        variant: "success",
        onCloseButton: closeSnackbar
      });
    } catch (error) {
      const status =
        (error as { response?: { status?: number } })?.response?.status || 500;
      console.error("Error setting coupon:", error);

      setErrorCoupon("Cupom inválido");

      let message = "Não foi possível aplicar o cupom, tente novamente";
      if (status === 400) {
        message = "Cupom inválido, tente novamente";
      } else if (status === 500) {
        message =
          "Identificamos uma instabilidade no sistema. Por favor, tente novamente mais tarde.";
      }

      setSnackbarData({
        show: true,
        message: message,
        variant: "error",
        onCloseButton: closeSnackbar
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      setLoading(true);
      const data = await removeCoupon(orderFormId);

      updateOrderForm(data);
      setCoupon("");
      setSnackbarData({
        show: true,
        message: "Cupom removido do carrinho",
        variant: "success",
        onCloseButton: closeSnackbar
      });
    } catch (error) {
      console.error("Error removing coupon:", error);
      setSnackbarData({
        show: true,
        message: "Não foi possível remover o cupom, tente novamente",
        variant: "error",
        onCloseButton: closeSnackbar
      });
    } finally {
      setLoading(false);
    }
  };

  const clearOrderFormMessages = async () => {
    try {
      const data = await clearMessages(orderFormId);
      updateOrderForm(data);
    } catch (error) {
      console.error("Error clearing order form messages:", error);
    }
  };

  const handleSelectSla = async ({
    selectedSla,
    deliveryType,
    deliveryWindow = null,
    orderForm
  }: {
    selectedSla: Sla;
    deliveryType: string;
    deliveryWindow?: any;
    orderForm: Order;
  }) => {
    try {
      const { shippingData, clientProfileData, items } = orderForm;

      let logisticInfo = await shippingData.logisticsInfo.map((info) => ({
        selectedDeliveryChannel: info.selectedDeliveryChannel,
        addressId: info.addressId,
        itemIndex: info.itemIndex,
        selectedSla: info.selectedSla
      }));
      const alteredProducts = await items.map((item) => item.index);

      let alteredDeliveryProducts = await logisticInfo.filter((info) =>
        alteredProducts.includes(info.itemIndex)
      );
      const unchangedDeliveryProducts = await logisticInfo.filter(
        (info) => !alteredProducts.includes(info.itemIndex)
      );

      alteredDeliveryProducts = await alteredDeliveryProducts.map(
        (logisticInfo) => ({
          ...logisticInfo,
          selectedDeliveryChannel: deliveryType,
          selectedSla: selectedSla.id,
          deliveryWindow
        })
      );

      logisticInfo = [...alteredDeliveryProducts, ...unchangedDeliveryProducts];

      return triggerShipping({
        logisticsInfo: logisticInfo,
        shippingData,
        clientProfileData,
        orderForm
      });
    } catch (error) {
      updateOrderForm(orderForm);
      console.error("Error selecting unavailable pickupId", error);
      throw error;
    }
  };

  const triggerShipping = async ({
    logisticsInfo,
    shippingData,
    clientProfileData,
    orderForm
  }: {
    logisticsInfo: LogisticsInfo[];
    shippingData: ShippingData;
    clientProfileData?: ClientProfileData;
    orderForm: Order;
  }) => {
    try {
      const { address } = shippingData;

      const addressPayload = {
        logisticsInfo: logisticsInfo,
        selectedAddresses: [address]
      };

      const data = await getShippingSimulate(
        addressPayload,
        orderFormId,
        clientProfileData?.email
      );

      await updateOrderForm(data);
    } catch (error) {
      errorMessageDefault();
      updateOrderForm(orderForm);
      console.error("Error selecting SLA", error);
      throw error;
    }
  };

  const modalProductUnavailable = () => {
    if (!orderFormId) return;

    const itemUnavailable = orderForm?.items?.map((item) => {
      return item.availability;
    });

    if (itemUnavailable.includes(STATUS.CANNOTBEDELIVERED)) {
      setSnackbarData({
        show: true,
        message:
          "Produto indisponível para esta região. Sugerimos que altere o CEP ou substitua o item por um similar de sua preferência.",
        variant: "error",
        onCloseButton: closeSnackbar
      });
      return true;
    } else if (
      itemUnavailable.some(
        (item) => item !== STATUS.AVAILABLE && item.includes("Medicamentos")
      )
    ) {
      setSnackbarData({
        show: true,
        message:
          "Produto indisponível no momento. Por favor, exclua o item para prosseguir.",
        variant: "error",
        onCloseButton: closeSnackbar
      });
      return true;
    } else if (itemUnavailable.some((item) => item !== STATUS.AVAILABLE)) {
      setSnackbarData({
        show: true,
        message:
          "Produto indisponível no momento. Sugerimos que exclua ou substitua os produtos indisponíveis para concluir a compra.",
        variant: "error",
        onCloseButton: closeSnackbar
      });
      return true;
    }
  };

  /**
   * Função que cria ou recupera o cartID da ONG
   */
  const initializeCartID = async () => {
    try {
      setLoadingCart(true);

      const newCart: Order = await createNewCart();
      await setMarketingData(newCart.orderFormId, {
        ...(newCart?.marketingData || {}),
        utmipage: "donation_cart"
      });

      const newDonationData: DonationData = {
        cartID: newCart.orderFormId,
        ongId: ong.id,
        ongName: ong.name,
        zipcode: ong.loja?.zipcode,
        pickupId: ong.vtex_pickup_id
      };

      const donationList = getDonationDataArray();

      const filteredDonationList = donationList.filter(
        (item) => item.ongId !== ong.id
      );

      filteredDonationList.push(newDonationData);

      window.localStorage.setItem(
        "donation",
        JSON.stringify(filteredDonationList)
      );

      setCartID(newCart.orderFormId);
      setDonationData(newDonationData);
      updateOrderForm(newCart);
    } catch (error) {
      console.error("Error on mount cart ID", error);
      return {
        orderFormId: "",
        newDonationData: []
      };
    } finally {
      setLoadingCart(false);
    }
  };

  /**
   * Função que hidrata o orderForm com os dados da Loja
   * Chamada logo após a criação do cartID
   */
  const mountDonationCart = async (donationData: DonationData) => {
    try {
      if (!orderFormId) return;

      setLoadingCart(true);

      // Busca o CEP da loja onde a ONG está cadastrada
      let responseFreighData = await updateFreightData(
        donationData.zipcode,
        orderFormId,
        orderForm.items.length
      );

      // Localiza e seta a opção de entrega atrelada a ONG
      const sla = responseFreighData.shippingData.logisticsInfo[0].slas.find(
        (sla: Sla) => sla.id.includes(donationData?.pickupId || "")
      );

      if (!sla) {
        // ToDo: Validar tratamento para o caso de não encontrar a SLA -> pickupId.
        /* setSnackbarData({
          show: true,
          message:
            "Não foi possível identificar seu Cesto de doação. Por favor, tente novamente mais tarde.",
          variant: "error",
          onCloseButton: closeSnackbar
        });*/

        setNotIdentifiedSla(true);

        setLoadingCart(false);
        Sentry.captureException(new Error("Doação - SLA não identificado."), {
          extra: {
            donationData,
            logisticsInfo: responseFreighData?.shippingData?.logisticsInfo
          }
        });

        console.error("Sla not identified", {
          donationData,
          responseFreighData
        });
      } else {
        setNotIdentifiedSla(false);
      }

      const ongNumber = sla.pickupStoreInfo.address.number;

      // Completa dados obrigatórios de endereço da ONG
      responseFreighData.shippingData.address.number = ongNumber;

      const payload = {
        selectedSla: sla,
        deliveryType: DELIVERY_CHANNEL_OPTIONS.PICKUP,
        orderForm: responseFreighData
      };

      // Seta as UtmParams no orderForm
      utmParams(orderFormId);

      await handleSelectSla(payload);
    } catch (error) {
      console.error("Error on mount donation cart", error);
    } finally {
      setLoadingCart(false);
    }
  };

  const initializeDonationCart = async (ong: Ong) => {
    const cartID = getCartID(ong.id);

    if (cartID) {
      setCartID(cartID);
      getCartData(cartID);
    } else {
      initializeCartID();
    }
  };

  const getCartData = async (cartID: string) => {
    try {
      const response = await getCart(cartID);
      updateOrderForm(response);
      setLoadingCart(false);
    } catch (error) {
      console.error("Error fetching cart data:", error);
      setLoadingCart(false);
    }
  };

  return {
    cartID,
    coupon,
    errorCoupon,
    donationData,
    isLoadingCart,
    getCartData,
    initializeCartID,
    mountDonationCart,
    initializeDonationCart,
    setGiftquantity,
    setGiftActive,
    handleRemoveProduct,
    handleUpdateQuantity,
    triggerUpdateQuantity,
    handleSetCoupon,
    handleRemoveCoupon,
    handleSelectSla,
    modalProductUnavailable,
    notIdentifiedSla
  };
};
